import type { IntegrationStageDateRange } from "./integration-filter-modal/integrationFilterTypes";
import {
  EMPTY_STAGE_DATE_RANGE,
  EMPTY_STAGE_FILTER,
  isValidationStage,
  type IntegrationFilterState,
  type IntegrationStageFilterFields,
} from "./integration-filter-modal/integrationFilterTypes";

const unique = (values: string[]): string[] =>
  Array.from(new Set(values.filter(Boolean)));

export const collectStageDateRanges = (
  stageFilters: Record<string, IntegrationStageFilterFields>
): Record<string, IntegrationStageDateRange> => {
  const result: Record<string, IntegrationStageDateRange> = {};

  for (const [stage, fields] of Object.entries(stageFilters)) {
    result[stage] = fields.dateRange;
  }

  return result;
};

export const flattenIntegrationStageFilters = (filter: IntegrationFilterState) => {
  const integrationStages: string[] = [];
  const integrationStatuses: string[] = [];
  const validationStatuses: string[] = [...filter.validationStatus];
  let validationScoreOperator: string | undefined;
  let validationScoreValue: string | undefined;
  let endorsementLevel: string[] = [];

  for (const stage of filter.integrationStages) {
    const fields = filter.stageFilters[stage];
    if (!fields) continue;

    if (isValidationStage(stage)) {
      validationStatuses.push(...fields.validationStatuses);
      endorsementLevel.push(...fields.endorsementLevel);

      const scoreValue = fields.validationScoreValue.trim();
      if (scoreValue && !Number.isNaN(Number(scoreValue))) {
        validationScoreOperator = fields.validationScoreOperator;
        validationScoreValue = scoreValue;
      }
      continue;
    }

    const hasIntegrationStatuses = fields.integrationStatuses.length > 0;
    const hasDates = hasStageDateValues(fields.dateRange);

    if (hasIntegrationStatuses || hasDates) {
      integrationStages.push(stage);
    }

    if (hasIntegrationStatuses) {
      integrationStatuses.push(...fields.integrationStatuses);
    }
  }

  return {
    integrationStages: unique(integrationStages),
    integrationStatuses: unique(integrationStatuses),
    validationStatuses: unique(validationStatuses),
    validationScoreOperator,
    validationScoreValue,
    endorsementLevel: unique(endorsementLevel),
    stageDateRanges: collectStageDateRanges(filter.stageFilters),
  };
};

export const hasStageDateValues = (range: IntegrationStageDateRange): boolean =>
  Boolean(
    range.startDateFrom ||
      range.startDateTo ||
      range.endDateFrom ||
      range.endDateTo
  );

export const createEmptyStageFilter = (): IntegrationStageFilterFields => ({
  ...EMPTY_STAGE_FILTER,
  dateRange: { ...EMPTY_STAGE_DATE_RANGE },
});
